import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import Webcam from 'react-webcam';
import {
  useVideoRecordRtc,
  MediaRecordingStatus,
  RecordingQuality,
  RecordingResult,
  SupportedMimeType,
  detectInAppBrowserByUserAgent,
  isIOS,
  createLogger,
  canSwitchCamera,
  FacingMode,
} from '@common/media';
import useChatStore from '../store';
import useSendMessage from './useSendMessage';

export const useVideoRecorder = () => {
  const { addMessageAndStartCreateAsistant, setShowVoiceMainInput } = useChatStore(
    (state) => state,
  );
  const { sendMessage, onSendMessageErrorHandler } = useSendMessage();

  const webcamRef = useRef<Webcam>(null);
  const logger = createLogger(true);

  // Use the simplified video recording hook (remove device enumeration)
  const {
    status: statusVideo,
    recordingTime,
    currentStream,
    isRecording,
    isPaused,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cleanup,
  } = useVideoRecordRtc();

  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | undefined>(undefined);
  const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [isVideoRecordingPressed, setIsVideoRecordingPressed] = useState(false);
  const [showVideoRecordingModal, setShowVideoRecordingModal] = useState(false);
  const [isVideoSending, setIsVideoSending] = useState(false);
  const [isVideoStopping, setIsVideoStopping] = useState(false);
  const [isVideoPausing, setIsVideoPausing] = useState(false);
  const [isVideoExiting, setIsVideoExiting] = useState(false);
  const [isVideoStarting, setIsVideoStarting] = useState(false);
  const [isVideoResuming, setIsVideoResuming] = useState(false);
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const [recordingResult, setRecordingResult] = useState<RecordingResult | null>(null);

  // recorded video blob
  const video: Blob | null = recordingResult?.blob || null;

  // Video constraints using facingMode approach for react-webcam
  const getVideoConstraints = useCallback(
    () => ({
      facingMode: isFrontCamera ? FacingMode.USER : FacingMode.ENVIRONMENT,
    }),
    [isFrontCamera],
  );

  // Update webcam ref when showing recorded video only
  useEffect(() => {
    if (
      webcamRef.current &&
      webcamRef.current.video &&
      videoPreviewUrl &&
      statusVideo === MediaRecordingStatus.STOPPED
    ) {
      // Show recorded video when stopped
      webcamRef.current.video.srcObject = null;
      webcamRef.current.video.src = videoPreviewUrl;
    }
  }, [videoPreviewUrl, statusVideo]);

  const handleStartVideoRecording = useCallback(async () => {
    setIsVideoStarting(true);
    try {
      // Clear previous recording
      setRecordingResult(null);
      setVideoPreviewUrl(undefined);

      // Pass react-webcam stream to RecordRTC
      const webcamStream = webcamRef.current?.stream || undefined;

      await startRecording(
        {
          quality: RecordingQuality.HIGH,
          facingMode: isFrontCamera ? FacingMode.USER : FacingMode.ENVIRONMENT,
          audio: true,
        },
        webcamStream,
      );
      setIsVideoRecordingPressed(true);
    } catch (error) {
      logger.error('Failed to start video recording:', error);
      toast.error('Failed to start video recording. Please try again.');
    } finally {
      setIsVideoStarting(false);
    }
  }, [startRecording, isFrontCamera]);

  const handleOpenVideoModal = useCallback(async () => {
    // Check for in-app browser and iOS Safari, and test camera
    const isInAppBrowser = detectInAppBrowserByUserAgent();

    if (isInAppBrowser && isIOS()) {
      try {
        // Simple camera check for iOS
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        const [track] = stream.getVideoTracks();
        track.stop(); // Clean up immediately
      } catch (error) {
        toast.warn('Camera is not supported. Please try with a different browser.');
        return;
      }
    }
    setShowVideoRecordingModal(true);
    setIsVideoRecordingPressed(true);
  }, []);

  const handleCloseVideoModal = () => {
    setShowVideoRecordingModal(false);
    setIsVideoRecordingPressed(false);
  };

  const handlePauseVideoRecording = useCallback(async () => {
    setIsVideoPausing(true);
    try {
      if (isRecording) {
        await pauseRecording();
      }
    } catch (error) {
      logger.error('Failed to pause video recording:', error);
      toast.error('Failed to pause video recording.');
    } finally {
      setIsVideoPausing(false);
    }
  }, [isRecording, pauseRecording]);

  const handleResumeVideoRecording = useCallback(async () => {
    setIsVideoResuming(true);
    try {
      if (isPaused) {
        await resumeRecording();
      }
    } catch (error) {
      logger.error('Failed to resume video recording:', error);
      toast.error('Failed to resume video recording.');
    } finally {
      setIsVideoResuming(false);
    }
  }, [isPaused, resumeRecording]);

  const handleStopVideoRecording = useCallback(async () => {
    setIsVideoStopping(true);
    try {
      if (isRecording || isPaused) {
        const result = await stopRecording();

        if (result) {
          setRecordingResult(result);
          setVideoPreviewUrl(result.url);
        }
      }
    } catch (error) {
      logger.error('Failed to stop video recording:', error);
      toast.error('Failed to stop video recording. Please try again.');
    } finally {
      setIsVideoStopping(false);
    }
  }, [isRecording, isPaused, stopRecording]);

  const handleExitVideoRecording = useCallback(async () => {
    setIsVideoExiting(true);
    try {
      cleanup();
      setRecordingResult(null);
      setVideoPreviewUrl(undefined);
      handleCloseVideoModal();
      setIsFrontCamera(true);
    } catch (error) {
      logger.error('Error during video recording cleanup:', error);
    } finally {
      setIsVideoExiting(false);
    }
  }, [cleanup]);

  // Camera switching using facingMode approach
  const handleFlipCamera = useCallback(async () => {
    if (!canSwitchCamera(true) || isSwitchingCamera) return;

    try {
      setIsSwitchingCamera(true);

      // Simple toggle - let react-webcam handle the stream management
      setIsFrontCamera((prev) => !prev);

      // Add delay for stream stabilization (Android needs this)
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } catch (error) {
      logger.error('Failed to switch camera:', error);
      toast.error('Failed to switch camera. Please try again.');
    } finally {
      setIsSwitchingCamera(false);
    }
  }, [isSwitchingCamera, isFrontCamera]);

  const sendVideoMessage = useCallback(
    async (videoBlob: Blob) => {
      try {
        // Convert Blob to File with proper filename and mimetype
        const extension = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';
        const type =
          extension === 'mp4' ? SupportedMimeType.VIDEO_MP4 : SupportedMimeType.VIDEO_WEBM;
        const filename = `video-${Date.now()}.${extension}`;
        const videoFile = new File([videoBlob], filename, { type });

        await sendMessage({
          file: videoFile,
          text: '',
          onError: onSendMessageErrorHandler,
        });

        addMessageAndStartCreateAsistant({
          _id: Math.random().toString(),
          role: 'user',
          text: '',
          loading: false,
          data: [
            {
              type: 'video',
              url: URL.createObjectURL(videoBlob),
              isUser: true,
            },
          ],
          created_at: new Date().toISOString(),
        });
      } catch {
        toast.error('Error sending video message.');
      }
    },
    [sendMessage, addMessageAndStartCreateAsistant],
  );

  const handleSubmitVideo = useCallback(async () => {
    if (!recordingResult?.blob || isVideoSending) return;
    setIsVideoSending(true);

    try {
      await sendVideoMessage(recordingResult.blob);
      await handleExitVideoRecording();
      setShowVideoRecordingModal(false);
      setIsVideoRecordingPressed(false);
      setIsFrontCamera(true);
    } catch (err) {
      logger.error('Error submitting video:', err);
      toast.error('Failed to send video message.');
    } finally {
      setIsVideoSending(false);
    }
  }, [recordingResult, sendVideoMessage, handleExitVideoRecording]);

  useEffect(
    () => () => {
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
        setVideoPreviewUrl(undefined);
      }
    },
    [videoPreviewUrl],
  );

  const generatePreview = useCallback(() => {
    if (recordingResult?.blob) {
      // The URL is already created in the recordRTC service callback
      // but we can ensure it's set here as well
      if (!videoPreviewUrl && recordingResult.url) {
        setVideoPreviewUrl(recordingResult.url);
      }
    }
  }, [recordingResult, videoPreviewUrl]);

  useEffect(() => {
    if (statusVideo === MediaRecordingStatus.STOPPED && recordingResult) {
      generatePreview();
    }
  }, [statusVideo, recordingResult, generatePreview]);

  // Computed values for compatibility with existing components
  const capturing =
    statusVideo === MediaRecordingStatus.RECORDING || statusVideo === MediaRecordingStatus.PAUSED;

  const isVideoRecordingActive = isRecording;

  return {
    statusVideo: statusVideo || MediaRecordingStatus.IDLE,
    previewStream: webcamRef,
    currentStream, // Add current stream for direct access
    isVideoRecordingActive,
    showVideoRecordingModal,
    handleStartVideoRecording,
    handleStopVideoRecording,
    handleExitVideoRecording,
    handlePauseVideoRecording,
    handleResumeVideoRecording,
    handleSubmitVideo,
    mediaBlobUrl: video,
    isVideoRecordingPressed,
    handleFlipCamera,
    isFrontCamera,
    handleOpenVideoModal,
    videoPreviewUrl,
    capturing,
    setShowVoiceMainInput,
    handleCloseVideoModal,
    isVideoSending,
    isVideoStopping,
    isVideoPausing,
    isVideoExiting,
    isVideoStarting,
    isVideoResuming,
    // New properties from simplified recordRTC
    recordingResult,
    recordingTime,
    // Camera switching availability and states
    canSwitchCamera: canSwitchCamera(true),
    isSwitchingCamera,
    // Video constraints for facingMode approach
    getVideoConstraints,
  };
};
